import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PageParams } from 'src/app/core/models/page-params.model';
import { ICountry } from '../../../../../core/models/country.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import { IShipmentPriceItemData, ISubCarrierPrice } from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';
import { PriceItemsDialogComponent } from '../../shipment-price-items/price-items-dialog/price-items-dialog.component';

@Component({
    selector: 'eci-pricing-sub-carriers',
    templateUrl: './sub-carriers.component.html',
    styleUrls: [ './sub-carriers.component.scss' ],
})
export class SubCarriersComponent implements OnInit {
    public form: FormGroup;

    @Input() public shipmentPriceItemId: number;
    @Input() public shipmentPriceItem: IShipmentPriceItemData;
    @Input() public subCarrierPrices: ISubCarrierPrice[];
    @Input() public theCountriesList: ICountry[];

    @ViewChild(MatTable) public table: MatTable<ISubCarrierPrice>;

    public currentSort: Sort = {active: 'createdAt', direction: 'desc'};
    public pageParams: PageParams = new PageParams();

    public dataSource: MatTableDataSource<ISubCarrierPrice>;
    public dynamicColumns: string[];
    public displayedColumns: string[] = [
        'destination',
        'costNetRates',
        'margin',
        'sellPrice',
        'unit',
        'subCarrier',
        'currency',
        'weight',
    ];
    public showLoader: boolean = false;

    constructor(
        public router: Router,
        private notify: NotificationService,
        private shipmentPriceService: ShipmentPriceService,
        public userSvc: UserService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource<ISubCarrierPrice>(this.subCarrierPrices);

        const prices = this.subCarrierPrices[0]?.prices;
        this.dynamicColumns = prices.map((p) => p.name);
        if (this.dynamicColumns.length > 0) {
            this.displayedColumns = [...this.displayedColumns, ...this.dynamicColumns];
        }
        this.displayedColumns.push('action');
        this.pageParams.length = this.dataSource.data.length;

        if (this.shipmentPriceItemId && !this.subCarrierPrices) {
            this.getList();
        }
    }

    private getList(page: number = this.pageParams.pageIndex + 1 || 1,
                    limit: number = this.pageParams.pageSize,
                    q: string = this.form?.get('search')?.value || '',
                    orderField: string = this.currentSort?.active || '',
                    orderDir: string = this.currentSort?.direction.toUpperCase() || ''): void {
        this.showLoader = true;
        this.shipmentPriceService.getPricingSubCarriersListByPriceItemId(
            this.shipmentPriceItemId, page, limit, q, {}, orderField, orderDir)
            .subscribe((data) => {
                    if (!data) {
                        return;
                    }
                    this.subCarrierPrices = data.items;
                    this.dataSource = new MatTableDataSource<ISubCarrierPrice>(data.items);
                    this.pageParams.pageSize = data.meta.itemsPerPage;
                    this.pageParams.pageIndex = data.meta.currentPage - 1;
                    this.pageParams.length = data.meta.totalItems;
                }, (err) => {
                    this.notify.showError(err.error.message);
                }, () => {
                    this.showLoader = false;
                }
            );
    }

    private getModel(data: { form: any; element: ISubCarrierPrice }): ISubCarrierPrice {
        const currentData = data.element;
        currentData.destination = +data.form.destination;
        currentData.costNetRates = data.form.costNetRates;
        currentData.margin = data.form.margin;
        currentData.sellPrice = data.form.sellPrice;
        currentData.unit = data.form.unit;
        currentData.subCarrier = data.form.subCarrier;
        currentData.currency = data.form.currency;
        currentData.shipmentPriceItemId = this.shipmentPriceItemId;

        Object.entries(data.form.prices)
            .forEach(([key, value]) => {
                const matchingObj = currentData.prices.find((obj1) => obj1.name === key);
                if (matchingObj) {
                    matchingObj.cost = value;
                } else {
                    currentData.prices.push({cost: value, name: key});
                }
            });

        return currentData;
    }

    private onResponseSuccess(element: ISubCarrierPrice, onSuccess: any, index: number): void {
        this.showLoader = false;
        onSuccess();
        this.subCarrierPrices[index] = element;
        this.dataSource.data = this.subCarrierPrices;
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList();
    }

    public sortData(sort: Sort): void {
        const data = this.dataSource;
        if (!sort.active || sort.direction === '') {
            this.dataSource = data;
            return;
        }
        this.currentSort = sort;
        this.getList();
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public addNewRow(event): void {
        if (!this.dataSource?.data) {
            this.dataSource = new MatTableDataSource<ISubCarrierPrice>([ event ]);
            this.shipmentPriceItemId = event.shipmentPriceItemId;
        }

        this.subCarrierPrices.push(event);
        this.dataSource.data = this.subCarrierPrices;
    }

    public removeData(index: number): void {
        this.subCarrierPrices.splice(index, 1);
        this.dataSource.data = this.subCarrierPrices;
    }

    public save(data: { form: any; element: ISubCarrierPrice; onSuccess: any; index: number }): void {
        if (data.element.id) {
            this.update(data);
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<ISubCarrierPrice>('sub-carrier-prices', this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Created successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public update(data: { form: FormGroup; element: ISubCarrierPrice; onSuccess: any; index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.update<ISubCarrierPrice>('sub-carrier-prices', data.element.id, this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Updated successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public delete(element: { id: number, index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.delete('sub-carrier-prices', element.id)
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Deleted successfully`);
                this.removeData(element.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public openUploadDialog(): void {
        const dialogRef = this.dialog.open(PriceItemsDialogComponent,
            {
                maxHeight: '800px',
                width: '900px',
            },
        );
        dialogRef.componentInstance.createMode = false;
        dialogRef.componentInstance.shipmentPrice = this.shipmentPriceItem;
        dialogRef.componentInstance.vendors = [this.shipmentPriceItem.vendor];
        dialogRef.componentInstance.services = [this.shipmentPriceItem.shipmentService];
        dialogRef.componentInstance.subServices = [this.shipmentPriceItem.subService];
        dialogRef.componentInstance.carriers = [this.shipmentPriceItem.carrier];
        dialogRef.componentInstance.title = 'Price Upload';
        dialogRef.afterClosed()
            .subscribe((result) => {
                this.getList();
            });
    }
}
